import React, { useState, useEffect, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import Fuse from 'fuse.js';
import { Layout } from '../templates';
import { PlayerHub, PlayerHubTableField } from '../organisms/PlayerHub';
import { useSettings } from '../../stateManagement/SettingsContext';
// eslint-disable-next-line import/namespace
import { TicketData } from '../organisms/TicketView';
import { useAuth } from '../../stateManagement/auth/useAuth';
import { BannerWithSearchBarForPlayerHub } from '../organisms/BannerWithSeachBarForPlayerHub';
// import { trackEvent } from '../../Analytics/ga';
import { appInsights } from '../../appInsights';
import {
  GlobalUserData,
  // hmac,
  initializeHelpshiftChat,
  listenToConversationStatus,
  log,
} from '../../utils';

const PLATFORM_ID = process.env.REACT_APP_WEB_PLATFORM_ID || '';
const DOMAIN = process.env.REACT_APP_DOMAIN || '';
const LANGUAGE = process.env.REACT_APP_LANGUAGE || '';
// const SECRET_KEY = process.env.REACT_APP_SECRET_KEY || '';

export const PlayerHubPage: React.FC = () => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [ticketResponse, setTicketResponse] = useState<any>(null);
  const [playerHubFields, setPlayerHubFields] = useState<PlayerHubTableField[]>([]);
  const [activeTicket, setActiveTicket] = useState<PlayerHubTableField | null>(null);
  const [inputValue, setInputValue] = useState('');
  const { user, accessToken } = useAuth();
  const userId: string = user?.idTokenClaims?.sub as string;
  const userEmail = user?.idTokenClaims?.emails?.[0] || '';
  const userName = user?.idTokenClaims?.name || '';
  // const steamId = user?.idTokenClaims?.steam_id as string;
  const globalUserData: GlobalUserData = {
    first_name: String(user?.idTokenClaims?.given_name || ''),
    last_name: String(user?.idTokenClaims?.family_name || ''),
    full_name: String(user?.idTokenClaims?.name || ''),
    display_name: String(user?.idTokenClaims?.name || ''),
    last_country: String(user?.idTokenClaims?.country || ''),
    last_city: String(user?.idTokenClaims?.city || ''),
    age: String(user?.idTokenClaims?.age || ''),
    lifetime_value: String(user?.idTokenClaims?.lifetimeValue || ''),
    user_persona: String(user?.idTokenClaims?.userPersona || ''),
    user_vip_segment: String(user?.idTokenClaims?.userVipSegment || ''),
    user_support_status: String(user?.idTokenClaims?.userSupportStatus || ''),
    last_active_date: String(user?.idTokenClaims?.lastActiveDate || ''),
    accepted_t_and_c: String(user?.idTokenClaims?.acceptedTAndC || false),
    preferred_language: String(user?.idTokenClaims?.preferredLanguage || ''),
  };
  // Build Fuse instance once
  // const fuse = new Fuse(playerHubFields, {
  //   keys: ['ticketSummary', 'status', 'date'], // Fields to search on
  //   threshold: 0.4, // Controls fuzziness: lower = stricter, higher = looser
  // });

  // const filteredTickets =
  //   inputValue.trim() === '' ? playerHubFields : fuse.search(inputValue).map(result => result.item);
  const fuse = useMemo(() => {
    if (!playerHubFields || playerHubFields.length === 0) return null;

    // Normalize text for better search results
    const normalizedData = playerHubFields.map(item => ({
      ...item,
      ticketSummary: item.ticketSummary?.toLowerCase() || '',
      status: item.status?.toLowerCase() || '',
      date: item.date?.toLowerCase() || '',
    }));

    return new Fuse(normalizedData, {
      keys: ['ticketSummary', 'status', 'date'],
      includeScore: true,
      threshold: 0.6, // higher = more lenient for typos
      distance: 200, // how far apart characters can be
      ignoreLocation: true, // ignore where in the string it matches
      minMatchCharLength: 1,
      useExtendedSearch: true,
    });
  }, [playerHubFields]);

  const filteredTickets = useMemo(() => {
    if (!fuse || inputValue.trim() === '') return playerHubFields;

    const query = inputValue.toLowerCase();

    // Run Fuse fuzzy search
    let results = fuse.search(query).map(result => result.item);

    // Fallback: If Fuse finds nothing, use manual substring matching
    if (results.length === 0) {
      results = playerHubFields.filter(item => {
        const summary = item.ticketSummary?.toLowerCase() || '';
        const status = item.status?.toLowerCase() || '';
        const date = item.date?.toLowerCase() || '';
        return summary.includes(query) || status.includes(query) || date.includes(query);
      });
    }
    return results;
  }, [fuse, inputValue, playerHubFields]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const fetchData = async () => {
    try {
      const cdnUrl = process.env.REACT_APP_ISSUES_URL;
      const today = new Date();
      const twoYearsAgo = new Date();
      twoYearsAgo.setFullYear(today.getFullYear() - 2);

      const currentYear = today.getFullYear();
      const lastYear = twoYearsAgo.getFullYear();
      const years = Array.from({ length: currentYear - lastYear + 1 }, (_, i) => lastYear + i);
      const allYearIssues = await Promise.all(
        years.map(async year => {
          const indexUrl = `${cdnUrl}/${userId}/${year}/closed-issues-index-file.jsonl`;

          try {
            const response = await fetch(indexUrl);
            if (!response.ok) throw new Error(`Failed for ${year}`);

            const rawText = await response.text();
            const indexList = rawText
              .trim()
              .split('\n')
              .map(line => {
                try {
                  return JSON.parse(line);
                } catch {
                  return null;
                }
              })
              .filter(entry => entry && !!entry.url);

            const issueResponses = await Promise.all(
              indexList.map(entry =>
                fetch(entry.url)
                  .then(res => res.json())
                  .catch(err => {
                    // Only warn once globally if needed
                    // eslint-disable-next-line no-console
                    console.warn(`Failed to fetch ${entry.url}:`, err);
                    return [];
                  }),
              ),
            );

            return issueResponses.flat();
          } catch (err) {
            // Just warn for the year, don't spam
            // eslint-disable-next-line no-console
            console.warn(`Error processing year ${year}`);
            return [];
          }
        }),
      );

      const allIssues = allYearIssues.flat();

      // Filter issues from the last 2 years
      const filteredIssues = allIssues.filter(issue => {
        const createdAt = new Date(issue.created_at);
        return createdAt >= twoYearsAgo && createdAt <= today;
      });
      // console.log('typeof filteredIssues', typeof filteredIssues);
      const uniqueIssuesMap = filteredIssues.reduce((acc, issue) => {
        const existing = acc.get(issue.id);
        if (!existing) {
          acc.set(issue.id, issue);
        } else {
          const existingDate = new Date(existing.updated_at);
          const currentDate = new Date(issue.updated_at);
          if (currentDate > existingDate) {
            acc.set(issue.id, issue);
          }
        }
        return acc;
      }, new Map());

      const uniqueIssues = Array.from(uniqueIssuesMap.values());
      setTicketResponse(uniqueIssues);

      // setTicketResponse(filteredIssues);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching issue data:', error);
    }
  };
  useEffect(() => {
    if (accessToken) fetchData();
  }, [accessToken]);

  // Ensure Helpshift initializes even after page refresh
  useEffect(() => {
    initializeHelpshiftChat({
      platformId: PLATFORM_ID,
      domain: DOMAIN,
      language: LANGUAGE,
      userName,
      userEmail,
      userId,
      // steam_id: steamId,
      globalUserData,
      accessToken,
      property: 'closeWidget',
      // userAuthToken: hmac(userId, userEmail, SECRET_KEY),
    });
    fetchData();
  }, []);

  const fetchConversationStatus = async () => {
    try {
      const status = await listenToConversationStatus();
      const activeIssues = status.open;
      log(settings?.configs.showLogs, 'Activeissues', activeIssues);

      if (activeIssues) {
        setActiveTicket({
          ticketNumber: Number(status.latestIssuePublishId) || 0,
          ticketSummary: 'Click here to view your active ticket',
          status: 'Active',
          date: '-',
        });
        initializeHelpshiftChat({
          platformId: PLATFORM_ID,
          domain: DOMAIN,
          language: LANGUAGE,
          userName,
          userEmail,
          userId,
          // steam_id: steamId,
          globalUserData,
          accessToken,
          property: 'openWidget,showWidget',
          // userAuthToken: hmac(userId, userEmail, SECRET_KEY),
        });
      }
    } catch (error) {
      log(settings?.configs.showLogs, 'Error fetching data:', error);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchConversationStatus();
    }
  }, [accessToken]);

  const transformData = (res: any[]): PlayerHubTableField[] => {
    return res.map((item: any) => ({
      ticketNumber: item.id,
      ticketSummary: item.title,
      status: item.state_data?.state || 'Unknown',
      date: item.updated_at
        ? new Date(item.updated_at).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: '2-digit',
          })
        : 'Unknown Date',
    }));
  };
  const onClickContactUs = () => {
    appInsights.trackEvent({
      name: 'contact_us_button_clicked',
      properties: { source: 'PlayerHubPage', buttonName: 'contactUsButton', userName: 'anonymous' },
    });
    navigate('/contactUs');
  };

  useEffect(() => {
    if (!ticketResponse) return;

    const closed = transformData(ticketResponse);
    const merged = activeTicket ? [activeTicket, ...closed] : closed;

    setPlayerHubFields(merged);
  }, [ticketResponse, activeTicket]);

  const transformTicketData = (data: any, issueId: number): TicketData | null => {
    if (!Array.isArray(data)) {
      // eslint-disable-next-line no-console
      console.warn('transformTicketData: expected data to be an array but got', data);
      return null;
    }

    const item = data.find((ticket: any) => ticket.id === issueId);
    if (!item) return null;

    return {
      ticketId: String(item.id),
      status: item.state_data?.state || 'Unknown',
      messages: item.messages
        ? item.messages
            .filter(
              (msg: any) =>
                !(
                  msg.origin === 'helpshift' &&
                  (msg.body === 'Bot Started' || msg.body === 'Bot Ended')
                ),
            )
            .map((msg: any) => ({
              sender: msg.origin === 'end-user' ? userName || 'You' : msg.author.name,
              timestamp: msg.created_at ? new Date(msg.created_at).toISOString() : 'Unknown Time',
              message: msg.body?.replace(/\n/g, '<br />') || 'No Message',
              origin: msg.origin,
            }))
        : [],
    };
  };

  const handleTicketClick = (issueId: number, status: 'Active' | 'resolved' | 'rejected') => {
    // trackEvent('ticket_viewed', {
    //   page: 'player_hub',
    // });

    const ticketData = transformTicketData(ticketResponse, issueId);

    if (status === 'Active') {
      initializeHelpshiftChat({
        platformId: PLATFORM_ID,
        domain: DOMAIN,
        language: LANGUAGE,
        userName,
        userEmail,
        userId,
        // steam_id: steamId,
        globalUserData,
        accessToken,
        property: 'openWidget',
        // userAuthToken: hmac(userId, userEmail, SECRET_KEY),
      });
    } else if (ticketData) {
      navigate(`/tickethistory/${issueId}`, { state: { ticketData } });
    }
  };

  const getTicketData = (issueId: number) => {
    return transformTicketData(ticketResponse, issueId);
  };

  const ticketHref = (issueId: number, status: 'Active' | 'resolved' | 'rejected') => {
    if (status === 'Active') {
      return '#';
    }
    return `/tickethistory/${issueId}`;
  };

  const { t } = useTranslation();
  const helperTexts = t('data.helperTexts', { returnObjects: true }) as any;

  return (
    <Layout
      pageBgColor={settings?.configs.secondaryBgColor}
      onContactUsClick={onClickContactUs}
      settings={settings}
    >
      <Helmet>
        <title>My Tickets</title>
      </Helmet>
      <Box
        display="flex"
        flexDirection="column"
        minHeight="76vh"
        backgroundColor={settings?.configs.secondaryBgColor}
      >
        <Box flex={1}>
          <BannerWithSearchBarForPlayerHub
            isFullScreen={settings?.configs.isBannerFullScreen}
            px={{
              base: '0vw',
              md: '0vw',
              lg: settings?.configs.isBannerFullScreen ? '0vw' : '15vw',
            }}
            imageSrc={settings?.configs.ticketViewPageLogoUrl}
            altText="Contact Us Banner"
            bannerText="My Tickets"
            bannerTextColor="white"
            placeholderText="Search Tickets"
            value={inputValue}
            onChange={handleSearchChange}
            bgColor={settings?.configs.secondaryBgColor}
            showJaggedBorder={settings?.configs.showJaggedBorder}
            secondaryAccentColor={settings?.configs.secondaryAccentColor}
            placeholderFontSize={{ base: '20px', md: '22px', lg: '24px' }} // Responsive font size
            placeholderFontWeight="bold"
          />

          <PlayerHub
            PlayerHubTableFields={filteredTickets}
            settings={settings ?? undefined}
            onTicketClick={handleTicketClick}
            emptyDataHelperText={
              helperTexts.find((item: Record<string, string>) => 'emptyDataHelperText' in item)
                ?.emptyDataHelperText ?? ''
            }
            ticketHref={ticketHref}
            getTicketData={getTicketData}
            // inputValue={inputValue}
          />
        </Box>
      </Box>
    </Layout>
  );
};
